import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

// Logo import
import logo345 from '../../assets/logo.png';

export interface FooterProps {
  website: {
    salonInfo: {
      name: string;
      address: string;
      city: string;
      district: string;
      phone: string;
      workingHours: {
        [key: string]: {
          isOpen: boolean;
          hours: Array<{
            start: string;
            end: string;
          }>;
        };
      };
    };
    content: {
      salonSlogan: string;
      shortDescription: string;
      footerDescription: string;
      socialMedia: {
        instagram?: string;
        facebook?: string;
        twitter?: string;
        youtube?: string;
        tiktok?: string;
      };
    };
  };
}

const Footer: React.FC<FooterProps> = ({ website }) => {
  const currentYear = new Date().getFullYear();
  const { salonInfo, content } = website;
  const { slug } = useParams<{ slug: string }>();

  // Çalışma saatlerini formatla
  const formatWorkingHours = (day: string) => {
    const dayData = salonInfo.workingHours[day];
    if (!dayData || !dayData.isOpen || !dayData.hours.length) {
      return 'Kapalı';
    }
    
    const hours = dayData.hours[0];
    return `${hours.start} - ${hours.end}`;
  };

  return (
    <FooterContainer>
      <div className="container">
        <FooterContent>
          <FooterSection>
            <FooterLogo>{salonInfo.name}</FooterLogo>
            <FooterDescription>
              {content.footerDescription || content.shortDescription}
            </FooterDescription>
            <SocialLinks>
              {content.socialMedia.instagram && (
                <SocialLink 
                  href={content.socialMedia.instagram.startsWith('http') ? content.socialMedia.instagram : `https://instagram.com/${content.socialMedia.instagram}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title="Instagram"
                >
                  <i className="fa-brands fa-instagram"></i>
                </SocialLink>
              )}
              {content.socialMedia.facebook && (
                <SocialLink 
                  href={content.socialMedia.facebook.startsWith('http') ? content.socialMedia.facebook : `https://facebook.com/${content.socialMedia.facebook}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title="Facebook"
                >
                  <i className="fa-brands fa-facebook-f"></i>
                </SocialLink>
              )}
              {content.socialMedia.twitter && (
                <SocialLink 
                  href={content.socialMedia.twitter.startsWith('http') ? content.socialMedia.twitter : `https://twitter.com/${content.socialMedia.twitter}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title="X/Twitter"
                >
                  <i className="fa-brands fa-twitter"></i>
                </SocialLink>
              )}
              {content.socialMedia.youtube && (
                <SocialLink 
                  href={content.socialMedia.youtube.startsWith('http') ? content.socialMedia.youtube : `https://youtube.com/${content.socialMedia.youtube}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title="YouTube"
                >
                  <i className="fa-brands fa-youtube"></i>
                </SocialLink>
              )}
              {content.socialMedia.tiktok && (
                <SocialLink 
                  href={content.socialMedia.tiktok.startsWith('http') ? content.socialMedia.tiktok : `https://tiktok.com/@${content.socialMedia.tiktok}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  title="TikTok"
                >
                  <i className="fa-brands fa-tiktok"></i>
                </SocialLink>
              )}
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Hızlı Erişim</FooterTitle>
            <FooterNav>
              <FooterNavItem>
                <FooterNavLink to={`/${slug}`}>Ana Sayfa</FooterNavLink>
              </FooterNavItem>
              <FooterNavItem>
                <FooterNavLink to={`/${slug}/hizmetler`}>Hizmetlerimiz</FooterNavLink>
              </FooterNavItem>
              <FooterNavItem>
                <FooterNavLink to={`/${slug}/galeri`}>Galeri</FooterNavLink>
              </FooterNavItem>
              <FooterNavItem>
                <FooterNavLink to={`/${slug}/iletisim`}>İletişim</FooterNavLink>
              </FooterNavItem>
              <FooterNavItem>
                <FooterNavLink to={`/${slug}/randevu`}>Randevu Al</FooterNavLink>
              </FooterNavItem>
            </FooterNav>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Çalışma Saatleri</FooterTitle>
            <WorkingHours>
              <WorkingHoursItem>
                <Day>Pazartesi:</Day>
                <Time>{formatWorkingHours('monday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Salı:</Day>
                <Time>{formatWorkingHours('tuesday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Çarşamba:</Day>
                <Time>{formatWorkingHours('wednesday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Perşembe:</Day>
                <Time>{formatWorkingHours('thursday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Cuma:</Day>
                <Time>{formatWorkingHours('friday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Cumartesi:</Day>
                <Time>{formatWorkingHours('saturday')}</Time>
              </WorkingHoursItem>
              <WorkingHoursItem>
                <Day>Pazar:</Day>
                <Time>{formatWorkingHours('sunday')}</Time>
              </WorkingHoursItem>
            </WorkingHours>
          </FooterSection>

          <FooterSection>
            <FooterTitle>İletişim</FooterTitle>
            <ContactInfo>
              <ContactItem>
                <i className="fa-solid fa-location-dot"></i>
                <ContactText>{salonInfo.address}, {salonInfo.district}/{salonInfo.city}</ContactText>
              </ContactItem>
              <ContactItem>
                <i className="fa-solid fa-phone"></i>
                <ContactText>
                  <a href={`tel:${salonInfo.phone.replace(/[^0-9]/g, '')}`}>
                    {salonInfo.phone}
                  </a>
                </ContactText>
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterDivider />

        <FooterBottom>
          <Copyright>
            &copy; {currentYear} {salonInfo.name}. Tüm hakları saklıdır.
          </Copyright>
          <FooterLinks>
            <FooterLink href="#">Gizlilik Politikası</FooterLink>
            <FooterLink href="#">Kullanım Koşulları</FooterLink>
          </FooterLinks>
        </FooterBottom>
        
        <DisclaimerContainer>
          <DisclaimerText>
            Bu sitedeki tüm işlemlerden {salonInfo.name} sorumludur. Randevu345 sadece yazılım hizmeti sağlar.
          </DisclaimerText>
        </DisclaimerContainer>
        
        {/* 345 Logo ve güvence metni */}
        <PoweredByContainer>
          <PoweredByText>
            Bu sistem 
            <LogoLink href="https://randevu.345.com.tr" target="_blank" rel="noopener noreferrer">
              <Logo345 src={logo345} alt="345 Logo" />
            </LogoLink> 
            teknolojisi ile çalışır.
          </PoweredByText>
        </PoweredByContainer>
      </div>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%);
  padding: 60px 0 20px;
  border-top: 1px solid #eaeaea;
  color: #333;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  margin-bottom: 20px;
`;

const FooterLogo = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FooterTitle = styled.h4`
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 20px;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  }
`;

const FooterDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 0.95rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
  
  i {
    font-size: 1rem;
  }
`;

const FooterNav = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterNavItem = styled.li`
  margin-bottom: 10px;
`;

const FooterNavLink = styled(Link)`
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
  display: block;
  
  &:hover {
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const WorkingHours = styled.div``;

const WorkingHoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Day = styled.span`
  color: #666;
  font-weight: 500;
`;

const Time = styled.span`
  color: #666;
`;

const ContactInfo = styled.div``;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  
  i {
    color: ${({ theme }) => theme.colors.primary};
    margin-right: 10px;
    margin-top: 3px;
    font-size: 1rem;
  }
`;

const ContactText = styled.div`
  color: #666;
  
  a {
    color: #666;
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
`;

const FooterDivider = styled.div`
  height: 1px;
  background: linear-gradient(to right, transparent, #ddd, transparent);
  margin: 30px 0;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
`;

const Copyright = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const FooterLink = styled.a`
  color: #666;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;
  
  &:hover {
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const DisclaimerContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  margin-bottom: 5px;
  text-align: center;
  padding: 10px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const DisclaimerText = styled.div`
  color: #777;
  font-size: 0.85rem;
  letter-spacing: 0.2px;
  line-height: 1.4;
`;

// 345 Logo için yeni styled componentler
const PoweredByContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eaeaea;
`;

const PoweredByText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 0.9rem;
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin: 0 5px;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const Logo345 = styled.img`
  height: 24px;
  width: auto;
`;

export default Footer; 